/* eslint-disable max-lines */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Ban, CheckCircle, Key, Package, Plus } from 'lucide-react';
import { formatDistanceToNow, format, isPast, parseISO } from 'date-fns';

import Button from '@components/atoms/ui-kit/button';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import Loader from '@components/atoms/loaders/loader';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useGetAdminLicenseKeys } from '@utils/api/back-office/licensing';
import { LicenseKey } from '@utils/types/back-office/licensing';
import RevokeLicenseModal from './revoke-license-modal';
import IssueDirectLicenseModal from './issue-direct-license-modal';
import DownloadOnPremBundleModal from './download-onprem-bundle-modal';

type Row = LicenseKey;

export default function LicenseKeysTab() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<LicenseKey | null>(null);
  const [issueDirectOpen, setIssueDirectOpen] = useState(false);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);

  const { data, isLoading } = useGetAdminLicenseKeys();

  const handleRevoke = useCallback((key: LicenseKey) => {
    setSelectedKey(key);
    setRevokeModalOpen(true);
  }, []);

  const formatExpiry = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      const isExpired = isPast(date);
      return {
        text: format(date, 'MMM d, yyyy'),
        relative: formatDistanceToNow(date, { addSuffix: true }),
        isExpired
      };
    } catch {
      return { text: '-', relative: '', isExpired: false };
    }
  };

  /* eslint-disable no-restricted-syntax -- Semantic colors for status badges and expired dates */
  const columns: DataGridColumn<Row>[] = useMemo(
    () => [
      {
        id: 'id',
        header: t('id'),
        accessorKey: 'id',
        maxWidth: 80,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'eq',
        cell: ({ getValue }) => `#${getValue()}`
      },
      {
        id: 'licensee',
        header: t('licensee'),
        accessorKey: 'licensee',
        minWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue())}</span>
        )
      },
      {
        id: 'expires',
        header: t('expires'),
        accessorKey: 'expires',
        size: 160,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const { text, relative, isExpired } = formatExpiry(
            getValue() as string
          );
          return (
            <div className="flex flex-col">
              <span
                className={cn('text-sm', isExpired && 'line-through')}
                style={{
                  color: isExpired ? '#DC2626' : getColour('normalText')
                }}
              >
                {text}
              </span>
              <span
                className="text-xs"
                style={{
                  color: isExpired ? '#DC2626' : getColour('inactiveText')
                }}
              >
                {relative}
              </span>
            </div>
          );
        }
      },
      {
        id: 'limits',
        header: t('limits'),
        accessorKey: 'org_limit',
        size: 230,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'gte',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.org_limit ?? '∞'} {t('orgs')} /{' '}
            {row.original.user_limit ?? '∞'} {t('users')}
          </span>
        )
      },
      {
        id: 'max_processing',
        header: t('processing'),
        accessorKey: 'max_processing',
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => `${getValue()} ${t('slots')}`
      },
      {
        id: 'realtime',
        header: t('realtime'),
        accessorKey: 'realtime',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          if (!row.original.realtime) {
            return <span style={{ color: getColour('inactiveText') }}>-</span>;
          }
          const langs = row.original.realtime_languages?.join(', ') ?? '';
          const streams = row.original.max_realtime_streams ?? '∞';
          return (
            <span className="text-xs" title={langs}>
              {streams} {t('streams')}
              {langs ? ` · ${langs}` : ''}
            </span>
          );
        }
      },
      {
        id: 'client_ip',
        header: t('last-seen-ip'),
        accessorKey: 'client_ip',
        size: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const ip = getValue() as string;
          return ip ? (
            <span
              className="text-xs font-mono px-2 py-0.5 rounded-full border border-hairline bg-app-soft"
              style={{ color: getColour('normalText') }}
            >
              {ip}
            </span>
          ) : (
            '-'
          );
        }
      },
      {
        id: 'status',
        header: t('status'),
        accessorKey: 'revoked_at',
        size: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const revokedAt = getValue() as string | null;
          const isRevoked = !!revokedAt;

          return (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium'
              )}
              style={{
                backgroundColor: isRevoked
                  ? 'rgba(239, 68, 68, 0.15)'
                  : 'rgba(16, 185, 129, 0.15)',
                color: isRevoked ? '#DC2626' : '#059669'
              }}
            >
              {isRevoked ? (
                <>
                  <Ban className="h-3 w-3" />
                  {t('revoked')}
                </>
              ) : (
                <>
                  <CheckCircle className="h-3 w-3" />
                  {t('active')}
                </>
              )}
            </span>
          );
        }
      },
      {
        id: 'last_modified',
        header: t('last-modified'),
        accessorKey: 'last_modified',
        minWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const date = getValue() as string;
          if (!date) return '-';
          try {
            return formatDistanceToNow(new Date(date), { addSuffix: true });
          } catch {
            return '-';
          }
        }
      }
    ],
    [t, getColour]
  );
  /* eslint-enable no-restricted-syntax */

  const actions: GridAction<Row>[] = useMemo(
    () => [
      {
        id: 'revoke',
        label: t('revoke'),
        icon: <Ban className="h-4 w-4" />,
        intent: 'danger',
        run: handleRevoke,
        isVisible: (row?: Row) => !row?.revoked_at
      }
    ],
    [t, handleRevoke]
  );

  const rows = data?.result ?? [];
  const totalItems = data?.total_results ?? 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4 pt-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('manage-issued-license-keys')}
        </p>
        <div className="flex items-center gap-3">
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {totalItems} {t('total-keys')}
          </p>
          <Button
            label={t('download-onprem-bundle')}
            icon={<Package width={14} />}
            size="small"
            variant="secondary"
            onClick={() => setBundleModalOpen(true)}
          />
          <Button
            label={t('issue-new-license')}
            icon={<Plus width={14} />}
            size="small"
            variant="primary"
            onClick={() => setIssueDirectOpen(true)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="w-full flex justify-center items-center pt-20">
          <Loader height={10} width={3} />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-hairline bg-app-soft">
          <div
            className="mb-4 h-16 w-16 rounded-full border-2 border-dashed border-hairline flex items-center justify-center"
            style={{ color: getColour('inactiveText') }}
          >
            <Key className="h-7 w-7" />
          </div>
          <p
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('no-license-keys')}
          </p>
          <p
            className="text-xs mt-1"
            style={{ color: getColour('inactiveText') }}
          >
            {t('license-keys-will-appear-here')}
          </p>
          <Button
            label={t('issue-new-license')}
            icon={<Plus width={14} />}
            size="small"
            variant="primary"
            onClick={() => setIssueDirectOpen(true)}
            className="mt-4"
          />
        </div>
      ) : (
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={totalItems}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.id)}
        />
      )}

      <RevokeLicenseModal
        isOpen={revokeModalOpen}
        onClose={() => {
          setRevokeModalOpen(false);
          setSelectedKey(null);
        }}
        licenseKey={selectedKey}
      />

      <IssueDirectLicenseModal
        isOpen={issueDirectOpen}
        onClose={() => setIssueDirectOpen(false)}
      />

      <DownloadOnPremBundleModal
        isOpen={bundleModalOpen}
        onClose={() => setBundleModalOpen(false)}
      />
    </div>
  );
}
